
import toastr from 'toastr'
import logger from '@/telemetry'

// utils
import ModelMapperApi from '@utils/modelMapperApi.compose'
import { orderBy } from 'lodash'
// services
import { FulfillmentCentersService } from '@/services/fulfillmentCenters'

// models
import { FulfillmentCentersModel, LocationsModel, FulFillmentCenterRulesModel, DestinationCountries, GlobalRegions, AddRuleModel, CountriesByRegionModel, LocationsShippableToCountriesModel, EnableDisableDeleteFcRuleModel } from '@/models/fulfillmentCenter'

// mappers
import { mapFulfillmentCenterWithTypeFromApi, mapLocationsFromApi, mapFulFillmentRulesFromApi, mapCountriesByRegionFromApi, mapGlobalRegionsFromApi, mapLocationsShippableToCountriesFromApi } from '@mappers/fulfillmentCenter'

export const ModuleName = 'fulfillmentCenter'
const ALL_COUNTRIES_ID = -1

export default {
  namespaced: true,
  state: {
    fulfillmentCenters: new LocationsModel(),
    spokeFulfillmentCenters: [],
    receivingFulfillmentCenters: new LocationsModel(),
    receivingFulfillmentCentersWithSpokeFCs: new LocationsModel(),
    hubFlowsEnabled: false,
    fulfillmentCenterRules: {
      rules: [],
      regions: []
    },
    countriesByRegion: new DestinationCountries(),
    locationsShippableToCountries: [],
    globalRegions: [],
    selectedGlobalRegion: null,
    selectedRegionCountries: [],
    fulfillmentCentersWithType: []
  },
  mutations: {
    setFulfillmentCenters (state, fcs) {
      state.fulfillmentCenters = fcs
      state.spokeFulfillmentCenters = fcs.where(fc => !fc.attributes.includes('Hub') && !fc.attributes.includes('Long Term Storage'))
    },
    setReceivingFulfillmentCenters (state, fcs) {
      state.receivingFulfillmentCenters = fcs
    },
    setReceivingFulfillmentCentersWithSpokeFCs (state, fcs) {
      state.receivingFulfillmentCentersWithSpokeFCs = fcs
    },
    setHubFlowsEnabled (state, enabled) {
      state.hubFlowsEnabled = enabled
    },
    setFulfillmentCenterRules (state, fcs) {
      state.fulfillmentCenterRules = fcs
    },
    setSelectedRegionCountries (state, countries) {
      state.selectedRegionCountries = countries
    },
    setCountriesByRegion (state, countriesByRegion) {
      state.countriesByRegion = countriesByRegion
    },
    setSelectedGlobalRegion (state, destinationRegion) {
      state.selectedGlobalRegion = destinationRegion
    },
    setLocationsShippableToCountries (state, options) {
      state.locationsShippableToCountries = options
    },
    setGlobalRegions (state, options) {
      state.globalRegions = options
    },
    setFulfillmentCentersWithType (state, fulfillmentCenters) {
      state.fulfillmentCentersWithType = fulfillmentCenters
    }
  },
  actions: {
    async getFulfillmentCenters ({ commit, dispatch, getters }, { loadingScopes }) {
      if (getters.fulfillmentCenters.length > 0) return
      const filterFCList = ModelMapperApi.get(
        ModelMapperApi.model(LocationsModel),
        ModelMapperApi.mapper(mapLocationsFromApi),
        ModelMapperApi.service(FulfillmentCentersService.get, FulfillmentCentersService)
      )
      try {
        const fcList = filterFCList()
        dispatch('addPromise', { promise: fcList, scopes: [...loadingScopes, 'getFulfillmentCenters'] }, { root: true })
        const fcListObject = await fcList
        commit('setFulfillmentCenters', fcListObject)
      } catch (ex) {
        const defaultFCListModel = new LocationsModel()
        commit('setFulfillmentCenters', defaultFCListModel)
      }
    },
    async getFulfillmentCentersWithType ({ commit, dispatch, getters }, { loadingScopes }) {
      if (getters.fulfillmentCentersWithType && getters.fulfillmentCentersWithType.length > 0) return

      const getFulfillmentCentersWithTypeFunc = ModelMapperApi.get(
        ModelMapperApi.model(FulfillmentCentersModel),
        ModelMapperApi.mapper(mapFulfillmentCenterWithTypeFromApi),
        ModelMapperApi.service(FulfillmentCentersService.getFulfillmentCentersWithType, FulfillmentCentersService)
      )
      try {
        const getFulfillmentCentersWithType = getFulfillmentCentersWithTypeFunc()
        dispatch('addPromise', { promise: getFulfillmentCentersWithType, scopes: [...loadingScopes, 'getFulfillmentCentersWithType'] }, { root: true })
        const result = await getFulfillmentCentersWithType
        commit('setFulfillmentCentersWithType', orderBy(result.fcList, 'id', 'asc'))
      } catch (ex) {
        logger?.trackException({
          exception: ex,
          severityLevel: 3
        }, {
          custom: 'getFulfillmentCentersWithType'
        })
        console.error(ex)
      }
    },
    async getReceivingFulfillmentCenters ({ commit, dispatch, getters }, { loadingScopes, includeSpokeFCs }) {
      if (includeSpokeFCs) {
        if (getters.receivingFulfillmentCentersWithSpokeFCs.length > 0) return
      } else {
        if (getters.receivingFulfillmentCenters.length > 0) return
      }

      const filterFCList = ModelMapperApi.get(
        ModelMapperApi.model(LocationsModel),
        ModelMapperApi.mapper(mapLocationsFromApi),
        ModelMapperApi.service(FulfillmentCentersService.getReceiving, FulfillmentCentersService)
      )
      try {
        const fcList = filterFCList(includeSpokeFCs)
        dispatch('addPromise', { promise: fcList, scopes: [...loadingScopes, 'getReceivingFulfillmentCenters'] }, { root: true })
        const fcListObject = await fcList
        if (includeSpokeFCs) {
          commit('setReceivingFulfillmentCentersWithSpokeFCs', fcListObject)
        } else {
          commit('setReceivingFulfillmentCenters', fcListObject)
        }
        await dispatch('getFulfillmentCentersWithType', { loadingScopes: [...loadingScopes, 'getReceivingFulfillmentCenters'] })
        const hubIds = getters.fulfillmentCentersWithType.filter(fc => fc.type === 5).map(fc => fc.id)
        commit('setHubFlowsEnabled', fcListObject.locations.some(fc => hubIds.includes(fc.id)))
      } catch (ex) {
        const defaultFCListModel = new LocationsModel()
        commit('setReceivingFulfillmentCenters', defaultFCListModel)
        commit('setHubFlowsEnabled', false)
      }
    },
    async getFulfillmentCenterRules ({ commit, dispatch }, { loadingScopes, regionId, searchString }) {
      try {
        const getFulfillmentRulesPromise = ModelMapperApi.post(
          ModelMapperApi.mapper(mapFulFillmentRulesFromApi),
          ModelMapperApi.service(FulfillmentCentersService.getFulfillmentCenterRules, FulfillmentCentersService),
          ModelMapperApi.model(FulFillmentCenterRulesModel)
        )
        const postToFulfillmentCenterRulesPromise = getFulfillmentRulesPromise({ regionId, searchString })
        dispatch('addPromise', { promise: postToFulfillmentCenterRulesPromise, scopes: [...loadingScopes, 'getFulfillmentCenterRules'] }, { root: true })
        const result = await postToFulfillmentCenterRulesPromise
        commit('setFulfillmentCenterRules', result)
      } catch (ex) {
        console.error(ex)
        const defaultFCListModel = {
          rules: [],
          regions: []
        }
        commit('setFulfillmentCenterRules', defaultFCListModel)
      }
    },

    async getCountriesByRegion ({ commit, dispatch, getters }, { loadingScopes }) {
      const region = getters.selectedGlobalRegion
      const getCountriesByRegionFunc = ModelMapperApi.post(
        ModelMapperApi.mapper(mapCountriesByRegionFromApi),
        ModelMapperApi.service(FulfillmentCentersService.getCountriesByRegion, FulfillmentCentersService),
        ModelMapperApi.model(CountriesByRegionModel)
      )

      const getCountriesByRegionPromise = getCountriesByRegionFunc({ region_id: region.id })
      dispatch('addPromise', { promise: getCountriesByRegionPromise, scopes: ['getCountriesByRegion'] }, { root: true })
      const result = await getCountriesByRegionPromise

      commit('setCountriesByRegion', [
        ...[{ id: -1, name: `All Countries in ${region.name}` }],
        ...orderBy(result, 'name', 'asc')
      ])
    },

    async getLocationsShippableToCountries ({ commit, dispatch, getters }, { loadingScopes, countries }) {
      const countryIds = countries.map((country) => country.id)

      const getLocationsShippableToCountriesFunc = ModelMapperApi.post(
        ModelMapperApi.mapper(mapLocationsShippableToCountriesFromApi),
        ModelMapperApi.service(FulfillmentCentersService.getLocationsShippableToCountries, FulfillmentCentersService),
        ModelMapperApi.model(LocationsShippableToCountriesModel)
      )

      const getLocationsShippableToCountriesPromise = getLocationsShippableToCountriesFunc({ country_ids: countryIds })
      dispatch('addPromise', { promise: getLocationsShippableToCountriesPromise, scopes: ['getLocationsShippableToCountries'] }, { root: true })
      const result = await getLocationsShippableToCountriesPromise
      commit('setLocationsShippableToCountries', orderBy(result, 'name', 'asc'))
    },
    async getGlobalRegions ({ commit, dispatch, getters }) {
      if (getters.globalRegions && getters.globalRegions.length > 0) return

      let result
      const getGlobalRegionsFunc = ModelMapperApi.get(
        ModelMapperApi.model(GlobalRegions),
        ModelMapperApi.mapper(mapGlobalRegionsFromApi),
        ModelMapperApi.service(FulfillmentCentersService.getGlobalRegions, FulfillmentCentersService)
      )
      try {
        result = await getGlobalRegionsFunc()
      } catch (ex) {
        console.error(ex)
      }

      commit('setGlobalRegions', orderBy(result.regions, 'name', 'asc'))
    },

    async postAddFulfillmentRule ({ commit, dispatch }, { loadingScopes, countryIds, fulfillmentCenterId, enabled }) {
      const postAddFulfillmentRuleFunc = ModelMapperApi.post(
        ModelMapperApi.service(FulfillmentCentersService.postAddFulfillmentRule, FulfillmentCentersService),
        ModelMapperApi.model(AddRuleModel)
      )

      const postToFulfillmentCenterRulesPromise = postAddFulfillmentRuleFunc({ country_ids: countryIds, fulfillment_center_id: fulfillmentCenterId, enabled })
      dispatch('addPromise', { promise: postToFulfillmentCenterRulesPromise, scopes: [...loadingScopes, 'postAddFulfillmentRule'] }, { root: true })

      try {
        await postToFulfillmentCenterRulesPromise
      } catch (ex) {
        toastr.error('Failed to save rule.')
        throw ex
      }
      toastr.success('Rule succesfully saved.')
    },

    async setSelectedGlobalRegion ({ dispatch, commit }, globalRegion) {
      commit('setSelectedGlobalRegion', globalRegion)
      if (globalRegion) {
        await dispatch('getCountriesByRegion', globalRegion)
      }
    },

    async setSelectedRegionCountries ({ getters, commit, dispatch }, countries) {
      commit('setSelectedRegionCountries', countries)
      if (countries.length) {
        const isAllCountriesSelected = countries.find((country) => country.id === ALL_COUNTRIES_ID)
        const selected = isAllCountriesSelected ? getters.countriesByRegion.filter((country) => country.id !== -1) : countries

        await dispatch('getLocationsShippableToCountries', { countries: selected })
      }
    },

    async postEnableFulfillmentCenterConfig ({ commit, dispatch }, { loadingScopes, UserFulfillmentCenterConfigIds }) {
      const postEnableFulfillmentCenterConfigFunc = ModelMapperApi.post(
        ModelMapperApi.service(FulfillmentCentersService.postEnableFulfillmentCenterConfig, FulfillmentCentersService),
        ModelMapperApi.model(EnableDisableDeleteFcRuleModel)
      )

      const postEnableFulfillmentCenterConfigPromise = postEnableFulfillmentCenterConfigFunc({ UserFulfillmentCenterConfigIds })
      dispatch('addPromise', { promise: postEnableFulfillmentCenterConfigPromise, scopes: [...loadingScopes, 'postEnableFulfillmentCenterConfig'] }, { root: true })

      try {
        await postEnableFulfillmentCenterConfigPromise
        toastr.success('Rule(s) succesfully enabled.')
      } catch (ex) {
        toastr.error('Failed to enable rule(s).')
      }
    },

    async postDisableFulfillmentCenterConfig ({ commit, dispatch }, { loadingScopes, UserFulfillmentCenterConfigIds }) {
      const postDisableFulfillmentCenterConfigFunc = ModelMapperApi.post(
        ModelMapperApi.service(FulfillmentCentersService.postDisableFulfillmentCenterConfig, FulfillmentCentersService),
        ModelMapperApi.model(EnableDisableDeleteFcRuleModel)
      )

      const postDisableFulfillmentCenterConfigPromise = postDisableFulfillmentCenterConfigFunc({ UserFulfillmentCenterConfigIds })
      dispatch('addPromise', { promise: postDisableFulfillmentCenterConfigPromise, scopes: [...loadingScopes, 'postDisableFulfillmentCenterConfig'] }, { root: true })

      try {
        await postDisableFulfillmentCenterConfigPromise
        toastr.success('Rule(s) succesfully disabled.')
      } catch (ex) {
        toastr.error('Failed to disable rule(s).')
      }
    },

    async postDeleteFulfillmentCenterConfig ({ commit, dispatch }, { loadingScopes, UserFulfillmentCenterConfigIds }) {
      const postDeleteFulfillmentCenterConfigFunc = ModelMapperApi.post(
        ModelMapperApi.service(FulfillmentCentersService.postDeleteFulfillmentCenterConfig, FulfillmentCentersService),
        ModelMapperApi.model(EnableDisableDeleteFcRuleModel)
      )

      const postDeleteFulfillmentCenterConfigPromise = postDeleteFulfillmentCenterConfigFunc({ UserFulfillmentCenterConfigIds })
      dispatch('addPromise', { promise: postDeleteFulfillmentCenterConfigPromise, scopes: [...loadingScopes, 'postDeleteFulfillmentCenterConfig'] }, { root: true })

      try {
        await postDeleteFulfillmentCenterConfigPromise
        toastr.success('Rule(s) successfully deleted.')
      } catch (ex) {
        toastr.error('Failed to delete rule(s).')
      }
    }
  },
  getters: {
    fulfillmentCenters: state => state.fulfillmentCenters.locations,
    receivingFulfillmentCenters: state => state.receivingFulfillmentCenters.locations,
    receivingFulfillmentCentersWithSpokeFCs: state => state.receivingFulfillmentCentersWithSpokeFCs.locations,
    hubFlowsEnabled: state => state.hubFlowsEnabled,
    fulFillmentCenterRules: state => state.fulfillmentCenterRules.rules,
    fulFillmentCenterRegions: state => state.fulfillmentCenterRules.regions,
    inventoryStatusViews: state => state.fulfillmentCenters.where(fc => fc.isActive && fc.accessGranted),
    isActive: state => state.fulfillmentCenters.where(fc => fc.isActive),
    accessGranted: state => state.fulfillmentCenters.where(fc => fc.accessGranted),
    orders: state => state.spokeFulfillmentCenters.filter(fc => fc.accessGranted && fc.isActive),
    workOrders: state => state.spokeFulfillmentCenters.filter(fc => fc.accessGranted && fc.isActive),
    createWro: state => state.receivingFulfillmentCenters.locations,
    createReturn: state => state.spokeFulfillmentCenters.filter(fc => fc.isActive && fc.accessGranted),
    activeLocations: state => state.spokeFulfillmentCenters.filter(fc => fc.isActive),
    globalRegions: state => state.globalRegions,
    locationsShippableToCountries: state => state.locationsShippableToCountries,
    countriesByRegion: state => state.countriesByRegion,
    selectedRegionCountries: state => state.selectedRegionCountries,
    selectedGlobalRegion: state => state.selectedGlobalRegion,
    fulfillmentCentersWithType: state => state.fulfillmentCentersWithType
  }
}
